<script setup>
import { computed } from 'vue'

import ModalBox from '@/components/modals/ModalBox.vue'
import { bytes as formatBytes, duration as formatDuration } from '@/filtersNew'

const props = defineProps({
	modelValue: {
		type: Boolean,
		default: false,
	},
	video: {
		type: Object,
		required: true,
	},
	stream: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['update:modelValue', 'close'])

const isVisible = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const displayName = computed(() => props.video?.fileName || props.video?.name || 'Video')

const durationLabel = computed(() => {
	const sec = props.video?.mediaInfo?.duration
	if (!sec) return ''
	const ms = sec * 1000
	return formatDuration(ms, ms >= 3600000 ? 'hh:mm:ss' : 'mm:ss', false)
})

const sizeLabel = computed(() => {
	const size = props.video?.size || props.video?.bytes
	if (!size) return ''
	return formatBytes(size, false, 1, true)
})

const metaLine = computed(() => {
	return [durationLabel.value, sizeLabel.value].filter(Boolean).join(' · ')
})

// Same preview-mode player URL as VODVideoGrid / VODVideoModal.
// A bare /vod/<id> is treated as a published stream ("Stream Offline");
// `preview=true` plays the file directly.
// Live-to-VOD files carry archive/embedUrl so the player can resolve them.
// Playout uploads currently have neither — `/vod/<id>?preview=true` 404s
// until the API registers a playback or returns embedUrl/srcHls.
const withPreviewParam = (uri) => {
	if (!uri) return ''
	if (uri.includes('preview=true')) return uri
	return `${uri}${uri.includes('?') ? '&' : '?'}preview=true`
}

const previewPlayerUrl = computed(() => {
	const v = props.video
	if (!v) return ''
	const base = process.env.VUE_APP_PLAYER_APP_BASE_URL
	if (v.dvr) {
		const range = `${v.from}-${v.duration}`
		return `${base}/vod/${v.streamKey}?range=${range}&preview=true&abr=${v.abr ? 'true' : 'false'}&server=${v.server}`
	}
	if (v.archive) {
		const range = `${v.archive.from}-${v.archive.duration}`
		return `${base}/${v.archive.key}?range=${range}&preview=true&abr=${v.archive.abr ? 'true' : 'false'}`
			+ (v.archive.namedHls ? '&namedHls=true' : '')
			+ (v.archive.cap ? '&cap=true' : '')
	}
	if (v.embedUrl) {
		const uri = String(v.embedUrl).replace('https://player.castr.io', base)
		return withPreviewParam(uri)
	}
	let vid = v.id || v._id || ''
	if (String(vid).split('.')[1] === 'mp4') vid = String(vid).replace('.mp4', '')
	return `${base}/vod/${vid}?preview=true`
})

const onClose = () => {
	isVisible.value = false
	emit('close')
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		title="Preview"
		size="md"
		header-class="!px-4 !py-3 sm:!px-4 sm:!py-3"
		wrapper-classes="lg:!h-auto lg:!max-h-none lg:!overflow-hidden overflow-hidden max-lg:!w-[calc(100vw-1rem)] max-lg:!max-w-[calc(100vw-1rem)]"
		@close="onClose"
	>
		<div class="relative aspect-video w-full bg-black">
			<iframe
				v-if="isVisible && previewPlayerUrl"
				:key="video.id || video._id"
				:src="previewPlayerUrl"
				class="absolute inset-0 w-full h-full"
				frameborder="0"
				allow="autoplay; encrypted-media; fullscreen"
				allowfullscreen
			/>
		</div>

		<template #footer>
			<div class="px-4 py-4 min-w-0">
				<p class="text-base font-medium text-white m-0 truncate" :title="displayName">{{ displayName }}</p>
				<p v-if="metaLine" class="text-tiny text-surface-7 m-0 mt-1">{{ metaLine }}</p>
			</div>
		</template>
	</ModalBox>
</template>
